import React, { useEffect, useRef, useState } from "react";
import { toast } from "./ui/use-toast";
import { API_BASE_URL } from "../config";
import {
  loadMainImage,
  loadAllThumbnails,
  renderHotspotStartpointDiv,
  getTourInfo,
  getRoomInfo,
  fetchWithRetry,
} from "../utils/tourUtils";
import {
  BUCKET_NAME_PHOTOS,
  FUNCTION_NAMES,
} from "../constants";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import TourSelector from './TourSelector';
import PannellumViewer from "../utils/360Viewer";
import PublicTourLinkManager from "./PublicTourLinkManager";
import { getDeviceCapabilities } from "../utils/imageUtils";
import SpaceTypeSelector from "./SpaceTypeSelector";
import { useParams } from "react-router-dom";
import { useMeasurements } from "../hooks/useMeasurements";
import MeasurementUI from "./MeasurementUI";

const TourEditorWrapper = (props) => {
  return <TourEditor {...props} />;
};

const TourEditor = () => {
  const { id: tourIdFromUrl } = useParams();
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [hotspots, setHotspots] = useState({});
  const [startPoints, setStartPoints] = useState({});
  const [mediaPhoto, setMediaPhoto] = useState("");
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [isSettingHotspot, setIsSettingHotspot] = useState(false);
  const [selectedTargetRoomId, setSelectedTargetRoomId] = useState(null);
  const deviceCapabilities = getDeviceCapabilities();
  const { maxWidth } = deviceCapabilities;
  const [latestYaw, setLatestYaw] = useState(180);
  const [latestPitch, setLatestPitch] = useState(0);
  const [npyFiles, setNpyFiles] = useState(new Set());
  const [depthMapSummary, setDepthMapSummary] = useState({
    total: 0,
    withDepth: 0,
    withoutDepth: 0,
  });
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);
  const pannellumRef = useRef(null);

  // Measurement functionality using hook
  const {
    measureMode,
    isProcessing,
    measurements,
    canMeasureCurrentRoom,
    toggleMeasurementMode,
    deleteMeasurement,
  } = useMeasurements(selectedTour, rooms, currentRoomId, pannellumRef);

  const fetchTours = async () => {
    try {
      const response = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: "tour.findMany",
          payload: {},
        },
        label: "fetch tours",
        throwError: true,
      });
      debugger;
      setTours(response);

      if (tourIdFromUrl) {
        const tour = response.find((t) => t.id === tourIdFromUrl);
        if (tour) {
          handleTourSelect(tour);
        } else {
          toast({
            title: "Error",
            description: "Tour not found",
            variant: "destructive",
          });
        }
      }
      // const response = await axios.get(`${API_BASE_URL}/api/get`, {
      //   params: { function_name: FUNCTION_NAMES.GET.GET_TOURS }
      // });
      // // debugger;
      // setTours(response.data);
    } catch (error) {
      console.error("Error fetching tours:", error);
      alert("Error loading tours. Please try again.");
    }
  };
  useEffect(() => {
    console.log("Latest view update →", { latestYaw, latestPitch });
  }, [latestYaw, latestPitch]);
  useEffect(() => {
    fetchTours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourIdFromUrl]);
  useEffect(() => {
    const handleRefetch = () => {
      fetchTours(); // 👈 your existing function that refetches tours
    };

    window.addEventListener("refetch-tours", handleRefetch);

    return () => {
      window.removeEventListener("refetch-tours", handleRefetch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateMainImage = async () => {
      if (selectedTour && currentRoomId) {
        const url = await loadMainImage(selectedTour, currentRoomId);
        setMediaPhoto(url);
      }
    };
    updateMainImage();
  }, [selectedTour, currentRoomId]);

  useEffect(() => {
    const updateThumbnails = async () => {
      // debugger;
      if (selectedTour) {
        const urls = await loadAllThumbnails(selectedTour, rooms);
        setThumbnailUrls(urls);
        // Fetch NPY files after loading thumbnails
        await fetchNpyFiles();
      }
    };
    updateThumbnails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTour]);

  // Analyze depth map coverage for current tour
  const analyzeDepthMapCoverage = () => {
    if (!rooms || rooms.length === 0)
      return { total: 0, withDepth: 0, withoutDepth: 0 };

    const total = rooms.length;
    const withDepth = rooms.filter((room) => checkNpyExists(room)).length;
    const withoutDepth = total - withDepth;

    return { total, withDepth, withoutDepth };
  };

  // Update depth map summary when npyFiles or rooms change
  useEffect(() => {
    if (rooms.length > 0 && npyFiles.size >= 0) {
      const summary = analyzeDepthMapCoverage();
      setDepthMapSummary(summary);
      console.log("Depth map coverage updated:", summary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npyFiles, rooms]);

  // Fetch all NPY files from S3
  const fetchNpyFiles = async () => {
    try {
      if (!selectedTour?.id) {
        console.log("No tour selected, skipping NPY fetch");
        return;
      }

      console.log("Fetching NPY files from S3...");
      const response = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.POST.LIST_S3_KEYS,
          payload: {
            bucketName: BUCKET_NAME_PHOTOS,
            prefixPath: `npy/${selectedTour.id}`,
          },
        },
        label: "fetching NPY files",
        throwError: true,
      });

      console.log("NPY files response:", response);

      // Create a Set of NPY filenames for efficient lookup
      const npySet = new Set(response.map((item) => item.Key));
      setNpyFiles(npySet);
      console.log(`Found ${npySet.size} NPY files:`, Array.from(npySet));

      // Note: Coverage analysis is now handled by useEffect above
    } catch (error) {
      console.error("Error fetching NPY files:", error);
    }
  };

  // Check if NPY exists for a room
  const checkNpyExists = (room) => {
    if (!room.imageKey || !selectedTour?.id) return false;
    const imageName = room.imageKey.split("/").pop().replace(".jpg", "");
    const npyKey = `npy/${selectedTour.id}/${imageName}.npy`;
    const exists = npyFiles.has(npyKey);
    console.log(`Checking NPY for room ${room.id}:`, {
      imageKey: room.imageKey,
      imageName,
      npyKey,
      exists,
      totalNpyFiles: npyFiles.size,
    });
    return exists;
  };

  const handleTourSelect = (tour) => {
    // debugger;
    // debugger;
    // Check if both 'imageKeys' and 'photos' exist and handle them accordingly
    const { newRooms, hotspots, startPoints } = getTourInfo(tour);

    setHotspots(hotspots);
    setStartPoints(startPoints);
    setSelectedTour(tour);
    setRooms(newRooms);
    if (newRooms && newRooms.length > 0 && newRooms[0].id) {
      setCurrentRoomId(newRooms[0].id); // ✅ set first image as default
    }
  };

  const handleRoomChange = (roomId) => {
    // debugger;
    const currentRoomInfoNew = getRoomInfo(rooms, roomId);
    if (currentRoomInfoNew) {
      setCurrentRoomId(currentRoomInfoNew.id);
    }
  };

  const toggleStartPoint = () => {
    setStartPoints((prev) => {
      const updated = { ...prev };

      if (updated[currentRoomId]) {
        delete updated[currentRoomId];
        // updated[currentRoom] = 'deleted';
      } else {
        updated[currentRoomId] = { yaw: latestYaw, pitch: latestPitch };
      }
      // debugger;
      return updated;
    });
  };

  const toggleHotspotMode = () => {
    setIsSettingHotspot((prev) => !prev);
    setSelectedTargetRoomId(null);
  };

  const saveHotspot = () => {
    // debugger;
    if (!selectedTargetRoomId) {
      alert("Please select a target room first");
      return;
    }

    // debugger;
    // alert(latestYaw);
    // alert(latestPitch);
    setHotspots((prev) => {
      const existing = prev[currentRoomId] || [];
      const newHotspot = {
        yaw: latestYaw,
        pitch: latestPitch,
        targetRoom: selectedTargetRoomId,
      };
      return {
        ...prev,
        [currentRoomId]: [...existing, newHotspot],
      };
    });

    setSelectedTargetRoomId(null);
    setIsSettingHotspot(false);
  };

  const deleteHotspot = (roomId, index) => {
    setHotspots((prev) => {
      const updatedRoomHotspots = [...(prev[roomId] || [])]; // clone array
      updatedRoomHotspots.splice(index, 1); // remove the hotspot

      return {
        ...prev,
        [roomId]: updatedRoomHotspots,
      };
    });
  };

  const saveTour = async () => {
    if (!selectedTour) {
      alert("No project selected. Please create a project first.");
      return;
    }
    try {
      // debugger;
      // Save the main tour (hotspots and startPoints)
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: "tour.update",
          payload: {
            id: selectedTour.id,
            data: {
              hotspots,
              startPoints,
            },
          },
        },

        label: "saving tour",
        throwError: true,
      });
      // alert('Tour saved successfully!');
      toast({
        title: `Tour saved successfully!`,
        description: `Tour saved successfully!`,
      });
      // Save tour images
      if (rooms.length > 0) {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: "tourImage.updateMany",
            payload: {
              data: rooms.map((room) => ({
                id: room.id,
                data: {
                  name: room.name,
                  description: room.description,
                },
              })),
            },
          },
          throwError: true,
          label: "saving tour images",
        });

        toast({
          title: `Tour images saved successfully!`,
          description: `Images saved successfully!`,
        });
      }
    } catch (error) {
      console.error("Error saving tour:", error);
      alert("Error saving tour. Please try again.");
    }
  };

  // const moveToNextRoom = () => {
  //   const currentRoomHotspots = Object.values(hotspots).filter(h => h.room === currentRoom);
  //   if (currentRoomHotspots.length > 0) {
  //     handleRoomChange(currentRoomHotspots[0].targetRoom);
  //   }
  // };
  //
  // const moveToPreviousRoom = () => {
  //   const previousRoomHotspot = Object.values(hotspots).find(h => h.targetRoom === currentRoom);
  //   if (previousRoomHotspot) {
  //     handleRoomChange(previousRoomHotspot.room);
  //   }
  // };
  const moveToNextRoom = () => {
    const index = rooms.findIndex((r) => r.id === currentRoomId);
    if (index !== -1 && index < rooms.length - 1) {
      handleRoomChange(rooms[index + 1].id);
    }
  };

  const moveToPreviousRoom = () => {
    const index = rooms.findIndex((r) => r.id === currentRoomId);
    if (index > 0) {
      handleRoomChange(rooms[index - 1].id);
    }
  };

  const generateBatchDepthMaps = async () => {
    if (!selectedTour || !rooms || rooms.length === 0) {
      toast({
        title: "Error",
        description: "No tour selected or no images found",
        variant: "destructive",
      });
      return;
    }

    if (isProcessingBatch) {
      toast({
        title: "Already Processing",
        description: "Batch processing is already in progress",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessingBatch(true);

      // Get all image keys from the tour
      const imageKeys = rooms
        .filter((room) => room.imageKey)
        .map((room) => room.imageKey);

      if (imageKeys.length === 0) {
        toast({
          title: "Error",
          description: "No images found in this tour",
          variant: "destructive",
        });
        return;
      }

      console.log(
        "Starting async depth map generation for tour:",
        selectedTour.id,
      );
      console.log("Image keys:", imageKeys);

      // Start async batch processing
      toast({
        title: "Starting Processing",
        description: `Starting depth map generation for ${imageKeys.length} images...`,
      });

      const startResponse = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        retries: 1,
        data: {
          function_name: FUNCTION_NAMES.POST.PROCESS_BATCH_DEPTH,
          payload: {
            image_keys: imageKeys,
            tour_id: selectedTour.id,
          },
        },
        label: "starting async batch depth maps",
        throwError: true,
      });

      const jobId = startResponse.jobId;
      const estimatedTime = startResponse.estimated_time_minutes;

      console.log("Batch job started:", jobId);

      toast({
        title: "Processing Started",
        description: `Job started! Estimated time: ${estimatedTime} minutes. Monitoring progress...`,
      });

      // Poll for status updates
      let isCompleted = false;
      let pollCount = 0;
      const maxPolls = 120; // Maximum 1 hour of polling (30 seconds * 120)

      while (!isCompleted && pollCount < maxPolls) {
        console.log(
          `Starting status check ${pollCount + 1}/${maxPolls} for job: ${jobId}`,
        );
        await new Promise((resolve) => setTimeout(resolve, 3000)); // Wait 30 seconds
        pollCount++;

        try {
          const statusResponse = await fetchWithRetry({
            url: `${API_BASE_URL}/api/post`,
            retries: 1,
            data: {
              function_name: FUNCTION_NAMES.POST.CHECK_BATCH_STATUS,
              payload: {
                job_id: jobId,
              },
            },
            label: "checking batch status",
            throwError: false,
          });

          console.log(`Status check ${pollCount}:`, statusResponse);

          if (statusResponse.status === "completed") {
            isCompleted = true;
            const successCount = statusResponse.success_count || 0;
            const errorCount = statusResponse.error_count || 0;

            toast({
              title: "Completed!",
              description: `Successfully processed ${successCount} images${errorCount > 0 ? `, ${errorCount} failed` : ""}`,
              variant: errorCount > 0 ? "destructive" : "default",
            });

            // Refresh NPY files after generation
            console.log("Refreshing NPY files after generation...");
            await fetchNpyFiles();
          } else if (statusResponse.status === "failed") {
            isCompleted = true;
            toast({
              title: "Processing Failed",
              description: statusResponse.error || "Batch processing failed",
              variant: "destructive",
            });
          } else if (statusResponse.status === "processing") {
            const progress = statusResponse.progress || {};
            const processed = progress.completed || 0;
            const total = progress.total || imageKeys.length;
            const percentage = progress.percentage || 0;

            toast({
              title: "Processing...",
              description: `Progress: ${processed}/${total} images (${percentage}%)`,
            });
          } else {
            // Status might be 'started' or 'not_found'
            if (statusResponse.status === "not_found") {
              isCompleted = true;
              toast({
                title: "Job Not Found",
                description:
                  "The processing job could not be found. It may have expired.",
                variant: "destructive",
              });
            } else {
              toast({
                title: "Processing...",
                description: `Job status: ${statusResponse.status || "unknown"}. Still processing...`,
              });
            }
          }
        } catch (statusError) {
          console.warn("Status check failed:", statusError);
          toast({
            title: "Status Check Failed",
            description: "Continuing to monitor... Check console for details.",
          });
        }
      }

      if (!isCompleted) {
        toast({
          title: "Timeout",
          description:
            "Processing is taking longer than expected. Check back later.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error starting batch depth map generation:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to start batch processing",
        variant: "destructive",
      });
    } finally {
      setIsProcessingBatch(false);
    }
  };

  const currentRoomInfo = getRoomInfo(rooms, currentRoomId);

  // Original component logic above...

  return (
    <div className="flex flex-col h-screen w-full overflow-y-auto">
      maxWidth:{maxWidth}
      {/* 1. Select Tour */}
      <div className="w-full p-4 border-b bg-white">
        <h2 className="text-lg font-bold mb-2">Select a Tour to Edit</h2>

        <TourSelector
          tours={tours}
          selectedTourId={selectedTour?.id}
          onTourSelect={(value) => {
            const tour = tours.find((t) => t.id === value);
            if (tour) handleTourSelect(tour);
          }}
          disabled={!!tourIdFromUrl}
          placeholder="Select a Tour"
        />

        {/* Depth Map Summary */}
        {selectedTour && depthMapSummary.total > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">
              Depth Map Coverage
            </h3>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-gray-600">Total Images:</span>
                <span className="font-semibold">{depthMapSummary.total}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-green-600">✓ With Depth:</span>
                <span className="font-semibold text-green-700">
                  {depthMapSummary.withDepth}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-red-600">✗ Missing:</span>
                <span className="font-semibold text-red-700">
                  {depthMapSummary.withoutDepth}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-blue-600">Coverage:</span>
                <span className="font-semibold text-blue-700">
                  {Math.round(
                    (depthMapSummary.withDepth / depthMapSummary.total) * 100,
                  )}
                  %
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* 2. Thumbnails + Room Info */}
      <div className="w-full p-4 border-b bg-gray-50">
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 gap-4">
          {rooms.map((room) => (
            <div key={room.id} className="relative">
              {/* Existing thumbnail rendering logic */}
              {renderHotspotStartpointDiv(
                [room],
                hotspots,
                startPoints,
                handleRoomChange,
                thumbnailUrls,
                currentRoomId,
              )}
              {/* NPY Depth Map Indicator */}
              {checkNpyExists(room) && (
                <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
                  NPY ✓
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="w-full mt-4 flex justify-end">
          <div className="w-full sm:w-1/2 md:w-1/4 space-y-2">
            {/*<h3 className="text-md font-medium mb-2 text-right">בחר חדר</h3>*/}
            <SpaceTypeSelector
              value={currentRoomInfo?.name || ""}
              onChange={(value) => {
                if (!currentRoomInfo) return;
                const updatedRoomInfo = { ...currentRoomInfo, name: value };
                setRooms((prevRooms) =>
                  prevRooms.map((room) =>
                    room.id === currentRoomInfo.id ? updatedRoomInfo : room,
                  ),
                );
              }}
              excludeValues={rooms
                .filter((room) => room.id !== currentRoomInfo?.id)
                .map((room) => room.name)}
            />
          </div>
        </div>
      </div>
      {/* 3. Navigation and Action Buttons */}
      {/* 4. 360 Viewer */}
      <div className="w-full flex justify-center bg-muted p-4 relative">
        <PannellumViewer
          canEdit={true}
          hotspotDebug={true}
          previewView={false}
          mediaPhoto={mediaPhoto}
          startPoints={startPoints}
          currentRoom={currentRoomId}
          currentRoomInfo={currentRoomInfo}
          hotspots={hotspots}
          rooms={rooms}
          handleRoomChange={handleRoomChange}
          onViewUpdate={({ yaw, pitch }) => {
            setLatestYaw(yaw);
            setLatestPitch(pitch);
          }}
          pannellumRef={pannellumRef}
        />
        
        {/* Measurement UI */}
        <MeasurementUI
          measureMode={measureMode}
          isProcessing={isProcessing}
          measurements={measurements}
          currentRoomId={currentRoomId}
          canMeasure={canMeasureCurrentRoom()}
          onToggleMeasurement={toggleMeasurementMode}
          onDeleteMeasurement={deleteMeasurement}
          mode="default"
        />
      </div>
      <div className="w-full p-4 border-b bg-white flex flex-wrap gap-4 justify-center items-center">
        <Button
          className={`w-32 ${startPoints[currentRoomId] ? "bg-blue-500" : "bg-gray-500"}`}
          onClick={toggleStartPoint}
        >
          {startPoints[currentRoomId] ? "Unset Start Point" : "Set Start Point"}
        </Button>
        <Button
          className={`w-32 ${isSettingHotspot ? "bg-blue-500" : "bg-gray-500"}`}
          onClick={toggleHotspotMode}
        >
          {isSettingHotspot ? "Cancel Hotspot" : "Add Hotspot"}
        </Button>
        {isSettingHotspot && (
          <div className="flex flex-wrap gap-2 items-center">
            <Select
              value={selectedTargetRoomId || ""}
              onValueChange={(value) => setSelectedTargetRoomId(value)}
            >
              <SelectTrigger>
                <SelectValue>
                  {selectedTargetRoomId
                    ? rooms.find((room) => room.name === selectedTargetRoomId)
                        ?.name
                    : "Select target room..."}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {rooms
                  .filter((room) => room.id !== currentRoomId)
                  .map((room) => (
                    <SelectItem key={room.id} value={room.id}>
                      {room.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              className="w-32"
              onClick={saveHotspot}
              disabled={!selectedTargetRoomId}
            >
              Save Hotspot
            </Button>
          </div>
        )}
        <Button className="w-32" onClick={moveToPreviousRoom}>
          Previous Room
        </Button>
        <Button className="w-32" onClick={moveToNextRoom}>
          Next Room
        </Button>
        {/*{startPoints[currentRoom] && Object.values(hotspots).filter(h => h.room === currentRoom).length > 0 && (*/}

        {/*)}*/}
        {/*{Object.values(hotspots).some(h => h.targetRoom === currentRoom) && (*/}
        {/* */}
        {/*)}*/}
        <Button
          className="w-32"
          onClick={saveTour}
          // disabled={!startPoints[currentRoomId] || Object.keys(hotspots).length === 0}
        >
          Save Tour
        </Button>
        <Button
          className="w-40 bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
          onClick={generateBatchDepthMaps}
          disabled={
            !selectedTour || 
            !rooms || 
            rooms.length === 0 || 
            isProcessingBatch ||
            (depthMapSummary.total > 0 && depthMapSummary.withDepth === depthMapSummary.total)
          }
        >
          {isProcessingBatch ? "Processing..." : 
           (depthMapSummary.total > 0 && depthMapSummary.withDepth === depthMapSummary.total) ? 
           "All Depth Maps Ready" : "Generate Depth Maps"}
        </Button>
      </div>
      {/* 5. Hotspot List */}
      <div className="w-full p-4 bg-white border-t">
        <h3 className="text-center font-bold mb-4">
          Hotspots in {currentRoomInfo?.roomName}
        </h3>
        {hotspots[currentRoomId]?.length > 0 ? (
          hotspots[currentRoomId].map((h, index) => (
            <div
              key={index}
              className="flex justify-between items-center p-2 border-b"
            >
              <div className="flex flex-col">
                <span>Yaw: {Math.round(h.yaw)}°</span>
                <span>Pitch: {Math.round(h.pitch)}°</span>
                <span>
                  Target:{" "}
                  {rooms.find((r) => r.id === h.targetRoom)?.name ||
                    "Room " + h.targetRoom}
                </span>
              </div>
              <Button
                variant="destructive"
                onClick={() => deleteHotspot(currentRoomId, index)}
              >
                X
              </Button>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500">
            No hotspots for this room yet.
          </p>
        )}
      </div>


      {selectedTour && (
        <div className="mt-8">
          <PublicTourLinkManager tourId={selectedTour.id} />
        </div>
      )}
    </div>
  );
};

export default TourEditorWrapper;
